import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faInfoCircle,
  faChevronRight,
  faSync,
  faPhoneVolume,
  faBookmark,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { memo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { ErrorToastContainer, getAvatarColor, getDisplayName, getInitials } from "../../../function/CustomFunction";
import ContactInfoOffcanvas from "./ContactInfoOffcanvas";

/**
 * Header — displays the active chat contact's info and action buttons.
 *
 * Props:
 *   wadetails        — contact object for the current chat
 *   onRefresh        — callback to re-fetch chat messages
 *   onBackClick      — callback to clear the active contact on mobile
 */
function Header({ wadetails, onRefresh, onBackClick, onStartCall }) {
  const [contactInfoOpen, setContactInfoOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const outgoingCallService = useSelector((state) => state?.serviceStore?.value?.outgoing_call);
  const isOutgoingCallEnabled = Boolean(outgoingCallService && outgoingCallService !== "0" && outgoingCallService !== 0);

  const openContactInfo = useCallback(() => setContactInfoOpen(true), []);
  const closeContactInfo = useCallback(() => setContactInfoOpen(false), []);

  const handleCallClick = useCallback(() => {
    if (!isOutgoingCallEnabled) {
      ErrorToastContainer("Your calling feature is not enabled");
      return;
    }
    if (typeof onStartCall === "function") {
      onStartCall(wadetails);
    }
  }, [onStartCall, wadetails, isOutgoingCallEnabled]);

  return (
    <div
      className="d-flex align-items-center justify-content-between chat-header p-3 border-bottom"
      style={{ backgroundColor: "#F0F2F5" }}
    >
      <div className="d-flex align-items-center gap-2">
        {/* Back button — mobile only */}
        <button
          className="btn btn-link p-0 d-lg-none"
          style={{ color: "rgb(39,92,83)", fontSize: "18px" }}
          onClick={onBackClick}
          aria-label="Back to contacts"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>

        {/* Avatar */}
        <div
          className="rounded-circle d-flex align-items-center justify-content-center fw-bold chat-avatar text-white shadow-sm"
          style={{
            width: "40px",
            height: "40px",
            backgroundColor: getAvatarColor(wadetails),
            color: "#ffffff",
            fontSize: "16px",
            cursor: "pointer",
            overflow: "hidden",
            flexShrink: 0,
            userSelect: "none",
          }}
          onClick={openContactInfo}
          aria-label={`View ${getDisplayName(wadetails)} info`}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === "Enter" && openContactInfo()}
        >
          {wadetails.profile_picture && wadetails.profile_picture !== "null" && wadetails.profile_picture !== "undefined" && !imgError ? (
            <img
              src={`https://api.campaigningtool.com/contact_picture/${wadetails.profile_picture}`}
              alt={wadetails.name || "contact"}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={() => setImgError(true)}
            />
          ) : (
            <span>{getInitials(wadetails)}</span>
          )}
        </div>

        {/* Name + WA ID */}
        <div style={{ cursor: "pointer" }} onClick={openContactInfo}>
          <div className="d-flex align-items-center gap-1">
            <h6 className="mb-0 fw-bold" style={{ fontSize: "15px", color: "#111B21" }}>
              {getDisplayName(wadetails)}
            </h6>
            <FontAwesomeIcon
              icon={wadetails.is_saved_contact ? faBookmark : faExclamationTriangle}
              style={{
                color: wadetails.is_saved_contact ? "#25D366" : "#FF9800",
                fontSize: "11px",
              }}
              title={wadetails.is_saved_contact ? "Saved contact" : "Not saved contact"}
            />
          </div>
          <div style={{ fontSize: "12px", color: "#6A7175" }}>{wadetails?.wa_id}</div>
        </div>

        {/* Info icon — mobile only */}
        <button
          className="btn btn-link p-0 d-xl-none ms-1"
          style={{ color: "rgb(39,92,83)", fontSize: "16px" }}
          onClick={openContactInfo}
          aria-label="Contact info"
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </button>
      </div>

      {/* Action Buttons */}
      <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
        {/* Call button — Voice calling support */}
        <button
          style={{ background: "none", border: "none", cursor: "pointer", color: "#111B21", padding: "6px" }}
          title={isOutgoingCallEnabled ? `Voice Call ${getDisplayName(wadetails)}` : "Calling feature not enabled"}
          aria-label={`Voice Call ${getDisplayName(wadetails)}`}
          onClick={handleCallClick}
        >
          <FontAwesomeIcon icon={faPhoneVolume} style={{ fontSize: "18px", color: "#25D366" }} />
        </button>

        {/* Refresh Messages */}
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={onRefresh}
          title="Refresh messages"
          aria-label="Refresh messages"
        >
          <FontAwesomeIcon icon={faSync} style={{ fontSize: "12px" }} />
        </button>

        {/* Contact info offcanvas (hidden DOM portal) */}
        <ContactInfoOffcanvas
          Contactinfo={contactInfoOpen}
          SetContactinfo={closeContactInfo}
          wadetails={wadetails}
        />

        {/* Chevron — desktop only */}
        <button
          className="btn btn-link p-0 d-none d-xl-block"
          onClick={openContactInfo}
          style={{ color: "#111B21", fontSize: "16px" }}
          aria-label="Open contact details"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    </div>
  );
}

export default memo(Header);